import { Heart, Check, Lightbulb, Star, ArrowRight, Calendar, TrendingUp, Sparkles, Loader2, Pencil } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ContentIdea } from "@/types/dashboard";
import { useFavorites } from "@/hooks/useFavorites";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

interface FeaturedContentIdeasProps {
  ideas: ContentIdea[];
  onCopyScript: (script: string) => void;
  onSeeMore: () => void;
  onIdeaClick?: (idea: ContentIdea) => void;
  onFavoriteToggle?: () => void;
  hasInspirationIdeas?: boolean;
  isWaitingForContent?: boolean;
  hasNoInspirationSources?: boolean;
}

const numberColors = [
  "bg-gradient-to-br from-purple-500 to-purple-600",
  "bg-gradient-to-br from-blue-500 to-blue-600",
  "bg-gradient-to-br from-orange-500 to-orange-600",
];

const placeholderIdeas = [
  { id: '1', title: 'Przygotowujemy dla Ciebie świetne pomysły...', description: 'Analiza trendów i inspiracji trwa.', viral_score: 85 },
  { id: '2', title: 'Twoje treści będą wkrótce gotowe', description: 'Sprawdzamy najlepsze źródła inspiracji.', viral_score: 78 },
  { id: '3', title: 'Szukamy viralowych tematów', description: 'Już niedługo zobaczysz wyniki.', viral_score: 72 },
];

export const FeaturedContentIdeas = ({ ideas, onCopyScript, onSeeMore, onIdeaClick, onFavoriteToggle, hasInspirationIdeas = true, isWaitingForContent = false, hasNoInspirationSources = false }: FeaturedContentIdeasProps) => {
  const { toggleFavorite, isLoading: isFavoriteLoading } = useFavorites();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleFavoriteToggle = async (idea: ContentIdea) => {
    const success = await toggleFavorite(idea.id, idea.is_favorited || false, idea.source);
    if (success) {
      onFavoriteToggle?.();
    }
  };

  const topThree = ideas
    .filter(idea => !idea.is_used)
    .slice(0, 3);

  const showPlaceholder = isWaitingForContent || topThree.length === 0;
  const displayItems = showPlaceholder ? placeholderIdeas : topThree;

  // Show CTA with blurred placeholders when user has no inspiration sources
  if (hasNoInspirationSources) {
    return (
      <div className="relative h-full" data-tour="featured-ideas">
        <div className="glass-card rounded-2xl p-6 md:p-8 flex flex-col h-full">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-amber-500" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                {t('dashboard.topContentIdeas')}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground ml-13">
              Twoje najlepsze pomysły z ostatnich 14 dni
            </p>
          </div>

          <div className="space-y-3 flex-1 relative">
            {/* Blurred placeholder overlay */}
            <div className="absolute inset-0 z-10 flex items-start justify-center pt-16 rounded-xl">
              <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] rounded-xl" />
              <div className="relative z-20 flex flex-col items-center gap-3 text-center px-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                </div>
                <p className="text-base font-semibold text-foreground">
                  Dodaj źródła inspiracji
                </p>
                <p className="text-sm text-muted-foreground max-w-[250px]">
                  Dodaj profile twórców, których lubisz i otrzymaj gotowe pomysły na treści
                </p>
                <Button
                  onClick={() => navigate('/inspiracje')}
                  size="sm"
                  className="mt-1"
                >
                  Uzupełnij
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Blurred placeholder items */}
            {placeholderIdeas.map((idea, index) => (
              <div 
                key={idea.id} 
                className="glass rounded-xl p-4 transition-all border border-border/50 blur-[3px] pointer-events-none select-none"
              >
                <div className="flex items-start gap-3">
                  <div className="relative w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-bold text-lg">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-success text-xs font-bold">
                        <TrendingUp className="w-3 h-3" />
                        <span>{idea.viral_score}</span>
                      </div>
                    </div>
                    <h3 className="font-bold text-base mb-1 text-foreground line-clamp-2">
                      {idea.title}
                    </h3>
                    {idea.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {idea.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full space-y-4" data-tour="featured-ideas">
      <div className="glass-card rounded-2xl p-6 md:p-8 flex flex-col h-full">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-5 h-5 text-amber-500" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {t('dashboard.topContentIdeas')}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground ml-13">
            Twoje najlepsze pomysły z ostatnich 14 dni
          </p>
        </div>

        <div className="space-y-3 flex-1 relative">
          {/* Blurred placeholder overlay */}
          {showPlaceholder && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl">
              <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] rounded-xl" />
              <div className="relative z-20 flex flex-col items-center gap-3 text-center px-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
                <p className="text-base font-semibold text-foreground">
                  Zbieramy pomysły, wróć za chwilę
                </p>
                <p className="text-sm text-muted-foreground max-w-[250px]">
                  Analizujemy Twoje źródła inspiracji
                </p>
              </div>
            </div>
          )}

          {displayItems.map((idea, index) => (
            <div 
              key={idea.id} 
              onClick={() => !showPlaceholder && onIdeaClick?.(idea as ContentIdea)}
              className={`glass rounded-xl p-4 transition-all border border-border/50 ${
                showPlaceholder 
                  ? 'blur-[3px] pointer-events-none select-none' 
                  : 'hover:border-primary/30 hover:shadow-md cursor-pointer'
              } group`}
            >
              <div className="flex items-start gap-3">
                {/* Number badge */}
                <div className="relative w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <span className="text-primary font-bold text-lg">{index + 1}</span>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Top row: viral score + favorite button */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {!showPlaceholder && (idea as ContentIdea).source === 'user_generated' ? (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                          <Pencil className="w-3 h-3" />
                          <span>Kreator</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-success text-xs font-bold">
                          <TrendingUp className="w-3 h-3" />
                          <span>{idea.viral_score || 0}</span>
                        </div>
                      )}
                      {!showPlaceholder && (idea as ContentIdea).is_used && (
                        <Badge variant="secondary" className="text-xs px-2 py-0.5">
                          <Check className="h-3 w-3 mr-1" />
                          {t('dashboard.used')}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full hover:bg-red-50 hover:scale-110 transition-all flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!showPlaceholder) handleFavoriteToggle(idea as ContentIdea);
                      }}
                      disabled={isFavoriteLoading || showPlaceholder}
                    >
                      <Heart
                        className={`w-4 h-4 transition-all ${
                          !showPlaceholder && (idea as ContentIdea).is_favorited 
                            ? "fill-red-500 text-red-500 scale-110" 
                            : "text-muted-foreground hover:text-red-500"
                        }`}
                      />
                    </Button>
                  </div>

                  {/* Title - full width, 2 lines max */}
                  <h3 className="font-bold text-base mb-1 text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {idea.title}
                  </h3>

                  {idea.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {idea.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            onClick={onSeeMore}
            variant="outline"
            className="group"
            disabled={showPlaceholder}
          >
            {t('common.seeMore')}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
};